// Extend Window interface for webkit prefix
declare global {
    interface Window {
        webkitAudioContext: typeof AudioContext;
    }
}

/**
 * Web Audio API 컨텍스트 관리
 */
export class AudioContextManager {
    private audioContext: AudioContext | null;
    private isClosing: boolean = false;

    constructor() {
        this.audioContext = null;
    }

    init(): void {
        if (!this.audioContext && !this.isClosing) {
            try {
                const AudioContextClass = window.AudioContext || window.webkitAudioContext;
                // iOS Safari: 낮은 샘플레이트 사용하여 메모리 사용량 감소
                const options: AudioContextOptions = {
                    latencyHint: 'playback'
                };
                this.audioContext = new AudioContextClass(options);

                // iOS Safari: state change 감지
                this.audioContext.addEventListener('statechange', () => {
                    if (this.audioContext?.state === 'interrupted') {
                        // iOS에서 인터럽트 시 자동 재개 시도
                        this.resume().catch(() => {});
                    }
                });
            } catch (e) {
                console.warn('Web Audio API not supported:', e);
            }
        }
    }

    get(): AudioContext | null {
        return this.audioContext;
    }

    async resume(): Promise<void> {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            try {
                await this.audioContext.resume();
            } catch (e) {
                console.warn('Failed to resume AudioContext:', e);
            }
        }
    }

    async suspend(): Promise<void> {
        if (this.audioContext && this.audioContext.state === 'running') {
            try {
                await this.audioContext.suspend();
            } catch (e) {
                console.warn('Failed to suspend AudioContext:', e);
            }
        }
    }

    unlock(): void {
        if (!this.audioContext) {
            this.init();
        }
        this.resume();
    }

    /**
     * iOS Safari 메모리 관리를 위한 컨텍스트 재생성
     */
    async recreate(): Promise<void> {
        if (this.audioContext) {
            this.isClosing = true;
            try {
                await this.audioContext.close();
            } catch (e) {
                // 이미 닫힌 경우 무시
            }
            this.audioContext = null;
            this.isClosing = false;
            this.init();
        }
    }

    /**
     * 오디오 컨텍스트 완전히 종료
     */
    close(): void {
        if (this.audioContext) {
            this.isClosing = true;
            this.audioContext.close().catch(() => {});
            this.audioContext = null;
        }
    }
}
